import type { Access } from './permissions';

/** Minimal current-user shape needed to render the owner (from the auth store). */
export interface OwnerCurrentUser {
  name: string;
  email?: string;
  picture?: string;
}

/** Resolved owner for display (name + optional email/avatar). */
export interface DerivedOwner {
  name: string;
  email?: string;
  avatarUrl?: string;
}

/**
 * Resolve the owner to display for an entity from its access info.
 *
 *  - `permission === 'owner'` (or no access yet) → the current logged-in user.
 *  - otherwise → `access.grantedBy` (the granter — the real owner/sharer; the
 *    server populates name/email/avatar from the granter's user row).
 *  - if a non-owner row somehow lacks `grantedBy` → fall back to current user.
 *
 * Fixes the bug where shared-with-me files rendered the *current user* as owner
 * (the library Owner column ignored access entirely). Mirrors the logic in
 * document-info-popover so every surface derives owner identically.
 *
 * Caveat (inherited): in a grant chain (Alice → Carol → Bob), Bob sees Carol,
 * not Alice — accurate chain-owner display needs a dedicated owner endpoint.
 */
export function deriveOwner(
  access: Access | null | undefined,
  currentUser: OwnerCurrentUser | null | undefined,
): DerivedOwner | null {
  const fromCurrent = (): DerivedOwner | null =>
    currentUser
      ? { name: currentUser.name, email: currentUser.email, avatarUrl: currentUser.picture }
      : null;

  if (!access || access.permission === 'owner') return fromCurrent();

  if (access.grantedBy) {
    return {
      name: access.grantedBy.name,
      email: access.grantedBy.email ?? undefined,
      avatarUrl: access.grantedBy.avatarUrl ?? undefined,
    };
  }

  return fromCurrent();
}
